import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import NavigationHandler from './components/NavigationHandler';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import MobileAppBanner from './components/MobileAppBanner';
import { useAuth } from './hooks/useAuth';
import { analytics } from './utils/analytics';
import ThinkingField from './components/ThinkingField';

// Root redirect: logged in → /home, not logged in → /login
const RootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
};

// Public pages
const AppStoreLandingPage = lazy(() => import('./pages/AppStoreLandingPage'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const MediaKitPage = lazy(() => import('./pages/MediaKitPage'));

// Auth pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Protected pages
const HomePage = lazy(() => import('./pages/HomePage'));
const GaragePage = lazy(() => import('./pages/GaragePage'));
const VehicleDetailPage = lazy(() => import('./pages/VehicleDetailPage'));
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const AskCarsjudgePage = lazy(() => import('./pages/AskCarsjudgePage'));
const ReceiptsPage = lazy(() => import('./pages/ReceiptsPage'));

// Business / fleet — the desktop half of the business accounts feature.
const FleetDashboardPage = lazy(() => import('./pages/business/FleetDashboardPage'));
const FleetTeamPage = lazy(() => import('./pages/business/FleetTeamPage'));
const FleetBillingPage = lazy(() => import('./pages/business/FleetBillingPage'));
const FleetReportsPage = lazy(() => import('./pages/business/FleetReportsPage'));
const FleetDocumentsPage = lazy(() => import('./pages/business/FleetDocumentsPage'));
const FleetCreatePage = lazy(() => import('./pages/business/FleetCreatePage'));
const FleetJoinPage = lazy(() => import('./pages/business/FleetJoinPage'));
const FleetPrivacyPage = lazy(() => import('./pages/business/FleetPrivacyPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const MaintenanceCostsPage = lazy(() => import('./pages/MaintenanceCostsPage'));
const UpcomingServicesPage = lazy(() => import('./pages/UpcomingServicesPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

const LoadingSpinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-bg">
    <div className="w-full max-w-md text-center">
      {/* The app's loading moment, not a generic spinner. It decelerates
          instead of looping, which is what makes it read as work happening
          rather than decoration. */}
      <ThinkingField height={200} />
      <p className="mt-2 text-sm text-content-secondary">One moment...</p>
    </div>
  </div>
);

const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location]);

  return null;
};

const AppContent = () => {
  const { state } = useAppContext();

  if (state.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router basename={import.meta.env.BASE_URL}>
      <RouteTracker />
      <CookieConsent />
      <NavigationHandler>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<AppStoreLandingPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/media-kit" element={<MediaKitPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />

            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected routes */}
            <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/garage" element={<ProtectedRoute><GaragePage /></ProtectedRoute>} />
            <Route path="/vehicle/:id" element={<ProtectedRoute><VehicleDetailPage /></ProtectedRoute>} />
            <Route path="/assessment" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
            <Route path="/ask" element={<ProtectedRoute><AskCarsjudgePage /></ProtectedRoute>} />
            <Route path="/receipts" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />

            {/* Car Lens is a camera feature and does not belong in a desktop
                browser; Community is hidden in the mobile app, so it is hidden
                here too rather than leaving web ahead of the product.
                Redirected instead of deleted: these paths are live today, and
                a bookmark or shared link should land somewhere useful rather
                than bouncing off the catch-all. */}
            <Route path="/car-lens" element={<Navigate to="/assessment" replace />} />
            <Route path="/car-lens/:id" element={<Navigate to="/assessment" replace />} />
            <Route path="/community" element={<Navigate to="/home" replace />} />
            <Route path="/community/create" element={<Navigate to="/home" replace />} />
            <Route path="/community/post/:id" element={<Navigate to="/home" replace />} />

            {/* Business / fleet */}
            <Route path="/business" element={<ProtectedRoute bare><FleetDashboardPage /></ProtectedRoute>} />
            <Route path="/business/team" element={<ProtectedRoute bare><FleetTeamPage /></ProtectedRoute>} />
            <Route path="/business/billing" element={<ProtectedRoute bare><FleetBillingPage /></ProtectedRoute>} />
            <Route path="/business/reports" element={<ProtectedRoute bare><FleetReportsPage /></ProtectedRoute>} />
            <Route path="/business/files" element={<ProtectedRoute bare><FleetDocumentsPage /></ProtectedRoute>} />
            <Route path="/business/create" element={<ProtectedRoute bare><FleetCreatePage /></ProtectedRoute>} />
            {/* NOT behind ProtectedRoute. A driver clicking their manager's
                invitation is usually signed out, and ProtectedRoute would
                bounce them to /login — discarding the ?token= in the process,
                so after signing in they would land on Home with no invitation
                and no way back to it. The page stashes the token and resumes
                after login instead. */}
            <Route path="/business/join" element={<FleetJoinPage />} />
            <Route path="/business/privacy" element={<FleetPrivacyPage />} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/user/:id" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/checkout/success" element={<ProtectedRoute bare><CheckoutSuccessPage /></ProtectedRoute>} />
            <Route path="/checkout/cancel" element={<ProtectedRoute bare><CheckoutCancelPage /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
            <Route path="/maintenance" element={<ProtectedRoute><MaintenanceCostsPage /></ProtectedRoute>} />
            <Route path="/upcoming-services" element={<ProtectedRoute><UpcomingServicesPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute bare><OnboardingPage /></ProtectedRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <MobileAppBanner />
      </NavigationHandler>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        {/* Inside AppProvider so it can rely on the session being managed
            there, and above the router so a page can ask which account the
            person is acting as without prop-drilling. */}
        <OrganizationProvider>
          <AppContent />
        </OrganizationProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
